///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Local copy of the USGS Slab2 subduction interface (0.1° nodes: slab-top
/// depth, strike, dip per zone; tools/slab2). Table-only; the earthquake
/// cutout samples it to draw the interface through the block.
///

import { Database } from "@/earthbrowser/database/Database"

const cBatchSize = 2000

export interface Slab2Row {
	zone: string
	lon: number
	lat: number
	depth_km: number
	dip_deg: number | null
	strike_deg: number | null
}

export class Slab2Table {
	public static async create(db: Database): Promise<void> {
		await db.bulkExec([
			{
				sql: `create table if not exists slab2 (
					zone text not null,
					lon real not null,
					lat real not null,
					depth_km real not null,
					dip_deg real,
					strike_deg real
				)`
			},
			{ sql: "create index if not exists idx_slab2_lat_lon on slab2(lat, lon)" },
			// Nodes are unique per zone; makes a re-ingest (dev reload mid-sync)
			// idempotent. Collapse any duplicates from before the index existed
			{ sql: "delete from slab2 where rowid not in (select min(rowid) from slab2 group by zone, lat, lon)" },
			{ sql: "create unique index if not exists idx_slab2_node on slab2(zone, lat, lon)" },
		])
	}

	public static async replaceAll(rows: Slab2Row[]): Promise<void> {
		await Database.db.exec("delete from slab2", [], "low")
		const statement = "insert or replace into slab2 (zone, lon, lat, depth_km, dip_deg, strike_deg) values (?, ?, ?, ?, ?, ?)"
		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(r => [r.zone, r.lon, r.lat, r.depth_km, r.dip_deg, r.strike_deg]), "low")
		}
	}

	public static async getCount(): Promise<number> {
		const rows = await Database.db.exec("select count(*) as count from slab2", [], "low") as Array<{ count: number }>
		return rows[0]?.count ?? 0
	}

	/** Nodes in a lat/lon box (degrees) */
	public static async queryBox(minLat: number, maxLat: number, minLon: number, maxLon: number): Promise<Slab2Row[]> {
		if (minLon < -180 || maxLon > 180) {
			const a = await this.queryBox(minLat, maxLat, Math.max(-180, minLon), Math.min(180, maxLon))
			const wrapped = minLon < -180
				? await this.queryBox(minLat, maxLat, minLon + 360, 180)
				: await this.queryBox(minLat, maxLat, -180, maxLon - 360)
			return a.concat(wrapped)
		}
		return await Database.db.exec(
			`select zone, lon, lat, depth_km, dip_deg, strike_deg from slab2
			where lat between ? and ? and lon between ? and ?`,
			[minLat, maxLat, minLon, maxLon],
			"high"
		) as Slab2Row[]
	}
}
